import { Button, css, Inline, Paragraph, Plus1Icon, styled } from 'canoe';
import { useActions, ENDPOINTS } from 'hammock';
import React, { useEffect, useState } from 'react';
import { s3Image } from 'site-constants';

import { useAuthentication, useNavigation } from 'commons/hooks';
import { useSessionStorage } from 'commons/hooks/useSessionStorage';

import { FolderTitle } from './FolderTitle';

const FormContainer = styled.div`
  ${({ theme }) => css`
    border: 1px solid ${theme.colors.GlitterGrayL10};
    border-radius: ${theme.borderRadius.small};
    display: flex;
    flex: 1;
    flex-direction: column;
    margin-bottom: ${theme.spaces.s};
    margin-top: ${theme.spaces.s};
    padding: ${theme.spaces.normal};
  `}
`;

const EmptySection = styled.div`
  ${({ theme }) => css`
    border: 1px solid ${theme.colors.GlitterGrayL10};
    border-radius: ${theme.borderRadius.small};
    background-image: url(${s3Image('/png/content_new_section.png')});
    background-position: bottom right;
    background-repeat: no-repeat;
    background-size: contain;
    display: flex;
    flex: 1;
    flex-direction: column;
    margin-top: ${theme.spaces.s};
    padding: ${theme.spaces.normal};
  `}
`;

interface AddSectionFormProps {
  autofocus?: boolean;
  inputRef?: React.MutableRefObject<HTMLInputElement | null>;
}

export const AddSectionForm = ({ inputRef, autofocus }: AddSectionFormProps) => {
  const navigate = useNavigation();
  const { applicationId } = useAuthentication();
  const [sectionFolderId, setSectionFolder] = useSessionStorage(
    `newSectionFolder.${applicationId}`,
    null,
  );

  const { getAll } = useActions(ENDPOINTS.contentFolder) as any;
  const [folder, setFolder] = useState<any>();

  const getFolder = async () => {
    const data = await getAll();
    const foundFolder = data.find(({ id }: any) => sectionFolderId === id);

    if (!foundFolder) {
      setSectionFolder(null);
      return;
    }

    setFolder(foundFolder);
  };

  useEffect(() => {
    if (sectionFolderId) {
      getFolder();
    }
  }, []);

  const onSubmit = async (values: any) => {
    if (!values) {
      return;
    }

    setFolder(values);
  };

  const onCreateNewContent = () => {
    if (folder?.id) {
      navigate(`/content/new?folderId=${folder.id}`);
    }
  };

  const onPickNewTemplate = () => {
    if (folder?.id) {
      navigate(`/content/templates?folderId=${folder.id}`);
    }
  };

  if (sectionFolderId && !folder) {
    return null;
  }

  return (
    <FormContainer>
      <FolderTitle
        initialFolder={folder}
        onSave={onSubmit}
        inputRef={inputRef}
        autofocus={autofocus}
      />
      <EmptySection>
        <Paragraph>Get started creating content. Start from scratch or pick a template!</Paragraph>
        <Inline>
          <Button
            appearance="outline"
            variant="secondary"
            icon={Plus1Icon}
            onClick={onCreateNewContent}
          >
            Create Content
          </Button>
          <Button appearance="outline" variant="contrast" onClick={onPickNewTemplate}>
            Pick a template
          </Button>
        </Inline>
      </EmptySection>
    </FormContainer>
  );
};
